import AuthApi from "api/authApi";
import {storeTokens} from "api";
import {
	IUser,
	ST2FAStatusResponse,
	ST2StepEnabledResponse,
	ST2StepVerifyNEnabledResponse,
	STAuthUserLoginResponse,
	STAuthVerifyAndEnable2FAAuth,
	STAuthVerifyTOTP,
} from "api/authApi/types";
import axios, {AxiosResponse} from "axios";
import {setItem} from "helpers/localStorage";
import {successResponse} from "helpers/map";
import {Mixpanel} from "helpers/mixpanel";
import {getTargetListsByOwnerID} from "store/target-list/targetListAsyncActions";
import {loginFailed, loginSuccess} from "store/user/userSlice";
import type {RTUser} from "store/user/types";
import {getUserChannel, getUserProfileInformation} from "store/user/userAsyncAction";
import {TAppDispatch, TErrorResponse} from "types";

// Type definitions for better type safety
type T2FAStatusResult = {enabled: boolean};
type T2FAEnableResult = ST2StepEnabledResponse | TErrorResponse | null;
type T2FADisableResult = ST2StepEnabledResponse | TErrorResponse | null;
type T2FAVerifyEnableResult = ST2StepVerifyNEnabledResponse | TErrorResponse | null;

/**
 * Helper function to extract error message from API response
 * Handles 401 unauthorized responses consistently
 */
const extractErrorFromResponse = (
	response: AxiosResponse<{detail?: string}> | undefined,
): TErrorResponse | null => {
	if (response?.status === 401) {
		return {
			error: response?.data?.detail || "Something went wrong!",
		};
	}

	return null;
};

export const get2FAStatus = () => async (): Promise<T2FAStatusResult> => {
	try {
		const response: AxiosResponse<ST2FAStatusResponse> = await AuthApi.get2FAAuthStatus();

		if (successResponse(response, 200)) {
			return {
				enabled: response.data.enabled,
			};
		}

		return {
			enabled: false,
		};
	} catch (e) {
		if (process.env.NODE_ENV === "development") {
			console.error("get2FAStatus error:", e);
		}

		return {
			enabled: false,
		};
	}
};

export const enable2FAAuth = (form: {password: string}) => async (): Promise<T2FAEnableResult> => {
	try {
		const response = await AuthApi.enable2FAAuth(form);

		if (successResponse(response, 200)) {
			return response.data as ST2StepEnabledResponse;
		}

		const errorResult = extractErrorFromResponse(response as AxiosResponse<{detail?: string}>);
		if (errorResult) {
			return errorResult;
		}

		return null;
	} catch (e) {
		if (process.env.NODE_ENV === "development") {
			console.error("enable2FAAuth error:", e);
		}

		return null;
	}
};

export const disable2FAAuth =
	(form: {password: string; code: string}) => async (): Promise<T2FADisableResult> => {
		try {
			const response = await AuthApi.disable2FAAuth(form);

			if (successResponse(response, 200)) {
				return response.data as ST2StepEnabledResponse;
			}

			const errorResult = extractErrorFromResponse(
				response as AxiosResponse<{detail?: string}>,
			);
			if (errorResult) {
				return errorResult;
			}

			return null;
		} catch (e) {
			if (process.env.NODE_ENV === "development") {
				console.error("disable2FAAuth error:", e);
			}

			return null;
		}
	};

export const verifyAndEnable2FAAuth =
	(form: STAuthVerifyAndEnable2FAAuth) => async (): Promise<T2FAVerifyEnableResult> => {
		try {
			const response = await AuthApi.verifyNEnable2FAAuth(form);

			if (successResponse(response, 200)) {
				return response.data as ST2StepVerifyNEnabledResponse;
			}

			const errorResult = extractErrorFromResponse(
				response as AxiosResponse<{detail?: string}>,
			);
			if (errorResult) {
				return errorResult;
			}

			return null;
		} catch (e) {
			if (process.env.NODE_ENV === "development") {
				console.error("verifyAndEnable2FAAuth error:", e);
			}

			return null;
		}
	};

export const verify2FAAuth =
	(form: STAuthVerifyTOTP) =>
	async (
		dispatch: TAppDispatch,
	): Promise<boolean | TErrorResponse | {message: string} | null> => {
		try {
			const response: AxiosResponse<STAuthUserLoginResponse> = await AuthApi.verify2FAAuth(
				form,
			);

			if (successResponse(response, 200)) {
				const userAccessData = response.data;

				return (await dispatch(fetchProfileAndSetUserState(userAccessData))) as unknown as
					| boolean
					| TErrorResponse
					| {message: string};
			} else {
				return {
					error: response?.data?.detail || "Something went wrong!",
				};
			}
		} catch (e) {
			if (process.env.NODE_ENV === "development") {
				console.error("verify2FAAuth error:", e);
			}

			return null;
		}
	};

export const fetchProfileAndSetUserState =
	(result: STAuthUserLoginResponse) =>
	async (dispatch: TAppDispatch): Promise<boolean | TErrorResponse | {message: string}> => {
		try {
			window.localStorage.clear();
			storeTokens(result);

			const user = (await dispatch(getUserProfileInformation())) as unknown as IUser;

			if (user?.is_active) {
				const pusherChannels = (await dispatch(getUserChannel())) as unknown as {
					app_key: string;
					channel: string;
				};

				const savedUser = {
					company: user.company,
					companyId: user.company_id,
					credit_refresh: user.credits_refresh_at,
					credits: user.credits,
					email: user.email,
					firstName: user.first_name,
					image: user.image,
					is_invenias_user: user.is_invenias || false,
					isActive: user.is_active,
					isSuperUser: user?.is_superuser || false,
					lastName: user.last_name,
					license: user.license,
					organisation_role: user.organisation_role,
					organisation: user.organisation,
					role: user.role,
					user_id: user.user_id,
					app_key: pusherChannels?.app_key,
					channel: pusherChannels?.channel,
				};

				setItem("user", JSON.stringify(savedUser));

				dispatch(loginSuccess(savedUser as unknown as RTUser));

				dispatch(getTargetListsByOwnerID());

				Mixpanel.identify(user.email);
				Mixpanel.track("logged in", {distinct_id: user.email});
				Mixpanel.people.set({
					$first_name: user.first_name,
					$last_name: user.last_name,
					$email: user.email,
					organisation: user.organisation?.name,
					distinct_id: user.email,
				});

				return true;
			} else {
				localStorage.clear();

				const error = {
					message:
						"This account is not active yet! Contact administrator for more information!",
				};

				dispatch(loginFailed({error}));

				Mixpanel.track("inactive user", {email: user.email, distinct_id: user.email});

				return error;
			}
		} catch (e) {
			if (axios.isCancel(e)) {
				if (process.env.NODE_ENV === "development") {
					console.log("Request canceled:", (e as Error).message);
				}
			}

			return {
				message: "Authentication failed!",
			};
		}
	};
